/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback, memo } from 'react'
import DatePicker, {
  PATHS_WITH_YEAR_AUTOCOMPLETION
} from 'components/Input/Date'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { makeStyles, Theme } from '@material-ui/core'

import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import { convertFormat } from './helpers'

const DATE_FORMAT = 'yyyy-MM-dd'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  helperTextStyle: {
    color: theme.palette.grey[800],
    marginTop: 0,
    marginBottom: '8px',
    position: 'absolute',
    top: '62px',
    padding: '10px',
    boxShadow: theme.shadows[3],
    zIndex: 5,
    background: 'white',
    borderRadius: '5px'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  }
}))

const DateInput = observer(
  ({
    path,
    visualFormat = 'dd.MM.yyyy',
    onTooltip,
    addDay = true,
    onChange,
    onBlur,
    placeholder = 'TT.MM.JJJJ',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelPath,
    ...restProps
  }: PathFormFieldProps) => {
    const fieldConfig = useMemo(
      () => ({
        format: (value: string | undefined) =>
          convertFormat(value || '', DATE_FORMAT, visualFormat),
        parse: (value: string | undefined) => {
          const dateString = convertFormat(
            value || '',
            visualFormat,
            DATE_FORMAT
          )

          if (
            addDay &&
            dateString.length === visualFormat.length &&
            dateString.length < DATE_FORMAT.length
          ) {
            return `${dateString}-01`
          }

          return dateString
        }
      }),
      [visualFormat, addDay]
    )

    const { input, meta, error } = usePatchedField(
      path,
      fieldConfig,
      true,
      PATHS_WITH_YEAR_AUTOCOMPLETION.includes(path)
        ? (value: string) => value.length === 10
        : undefined
    )

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { errorMap, ...restMeta } = useMeta(path)

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const changeHandler = useCallback(
      (_: Date | null, raw?: string | null) => {
        input.onChange(raw)
        if (onChange) {
          onChange(raw as string)
        }
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path]
    )
    const blurHandler = useCallback(() => {
      input.onBlur()
      if (onBlur) {
        onBlur(input.value as string)
      }
    }, [input])
    const focusHandler = useCallback(() => {
      input.onFocus()
    }, [input])
    const { container } = useStyles()
    return (
      <WithFieldInteraction path={path}>
        <div className={container}>
          <DatePicker
            {...restMeta}
            {...restProps}
            {...input}
            error={meta.touched ? error : ''}
            color="primary"
            placeholder={placeholder}
            format={visualFormat}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            onTooltip={onTooltip}
            path={path}
          />
        </div>
      </WithFieldInteraction>
    )
  }
)

const PathDateInput = (props: PathFormFieldProps) => {
  const { path, labelPath } = props
  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <DateInput {...props} />
    </FieldDataCollector>
  )
}

export default memo(PathDateInput)
